import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Topbar from "../components/Topbar";
import { toast } from 'react-toastify';
import { Container, Accordion, Row, Alert, Spinner } from "react-bootstrap";
import { AppsAPI } from "../api/apps";
import ChatBox from "../components/ChatBox";
import EnquireModal from "../components/EnquireModal";


function AppDetails() {
  const { slug } = useParams();

  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [loadingApp, setLoadingApp] = useState(false);
  const [showChatbox, setShowChatBox] = useState(false);
  const [showEnquireModal, setShowEnquireModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadApp() {
      try {
        setLoadingApp(true);
        setError("");
        const data = await AppsAPI.getApp(slug);
        if (!cancelled) setApp(data);
      } catch (e) {
        if (!cancelled) {
          setError("Could not fetch app. Please try again.");
          setApp(null);
        }
      } finally {
        if (!cancelled) setLoadingApp(false);
      }
    }
    loadApp();
    return () => { cancelled = true; };
  }, [slug]);

  const onSubmitEnquire = async (formData) => {
    await AppsAPI.submitEnquire(formData);
    toast.success("Your message submitted");
    setShowEnquireModal(false);
  }

  const isEmpty = !loadingApp && !app;

  return (
    <div className="wrapper min-vh-100">
      <Topbar />

      {loadingApp ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" className="mb-3" />
            <p>Loading...</p>
          </div>
        </div>
      ) : (
        <Container>
          {isEmpty ? (
            <div className="text-center py-5">
              <div className="mx-auto bg-light border rounded-4 d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 48, height: 48 }}>🔍</div>
              <h5 className="fw-semibold">No app found</h5>
              <p className="text-secondary">Please go back and try again.</p>
            </div>
          ) : (
            <>
              {error && (
                <Alert variant="warning" className="mb-4 py-2">{error}</Alert>
              )}

              <section className="py-5">
                <Row className="g-3 align-items-center">
                  <div className="col-12 col-lg-6">
                    <span className="badge rounded-pill text-bg-primary bg-opacity-10 text-primary border border-primary-subtle fw-semibold mb-3">{app?.primaryTag || (app?.tags?.[0] ?? "")}</span>
                    {app?.banner?.heading && (
                      <h1 className="display-6 fw-semibold mb-2">{app?.banner?.heading}</h1>
                    )}
                    {app?.banner?.subheading && (
                      <p className="fs-5 text-secondary mb-2">{app?.banner?.subheading}</p>
                    )}
                    {app?.banner?.text && (
                      <p className="text-secondary mb-5">{app?.banner?.text}</p>
                    )}
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-primary rounded-pill px-3" onClick={() => setShowEnquireModal(true)}>Enquire Now</button>
                      <button type="button" className="btn btn-outline-primary rounded-pill px-3" onClick={() => setShowChatBox(true)}>Explore AI Assistant</button>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <span className="badge text-bg-light border fw-semibold">What this app enables</span>
                        {app?.feature?.text1 && (
                          <p className="mt-2 mb-2">{app?.feature?.text1}</p>
                        )}
                        {app?.feature?.text2 && (
                          <p className="text-secondary mb-3">{app?.feature?.text2}</p>
                        )}
                        {app?.feature?.flows?.length && (
                          <ul className="list-unstyled mb-0">
                            {app?.feature?.flows.map((t, idx) => (
                              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                                <span className="badge rounded-pill text-bg-success bg-opacity-10 text-success border border-success-subtle">✓</span>
                                <span>{t}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </Row>
              </section>

              <section className="py-5">
                {app?.capabilities?.heading && (
                  <h2 className="h4 mb-3">{app?.capabilities?.heading}</h2>
                )}
                {app?.capabilities?.content?.length && (
                  <Row className="g-3">
                    {app?.capabilities?.content?.map((c) => (
                      <div key={c.title} className="col-12 col-sm-6 col-lg-4">
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            {c.icon.type === 'text' && (
                              <div className="d-inline-flex align-items-center justify-content-center rounded-3 border text-primary fw-bold mb-2" style={{ width: 40, height: 40, background: '#f1f4ff', borderColor: '#e9edff' }}>{c.icon.content}</div>
                            )}
                            <h3 className="h6 mb-1">{c.title}</h3>
                            <p className="text-secondary mb-0">{c.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Row>
                )}
              </section>

              <section className="py-5">
                {app?.steps?.heading && (
                  <h2 className="h4 mb-3">{app?.steps?.heading}</h2>
                )}
                {app?.steps?.content?.length && (
                  <>
                    <Row className="g-3">
                      {app?.steps?.content?.map((s, i) => (
                        <div key={s.title} className="col-12 col-lg-4">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <span className="badge text-bg-light border fw-semibold mb-2">{s.text}</span>
                              <h3 className="h6 mb-1">{s.title}</h3>
                              <p className="text-secondary mb-0">{s.desc}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Row>
                    <div className="d-flex justify-content-center mt-3">
                      <button type="button" className="btn btn-primary rounded-pill px-4" onClick={() => setShowEnquireModal(true)}>Enquire Now</button>
                    </div>
                  </>
                )}
              </section>

              <section className="py-5">
                {app?.outcomes?.heading && (
                  <h2 className="h4 mb-3">{app?.outcomes?.heading}</h2>
                )}
                {app?.outcomes?.content?.map((s, i) => (
                  <div className="card shadow-sm mb-3" key={i}>
                    <div className="card-body">
                      <span className="badge text-bg-light border fw-semibold">{s.name}</span>
                      <p className="fst-italic mt-2 mb-1">{s.comment}</p>
                      <p className="mb-3">{s.position}</p>
                      <div className="row g-2">
                        {s?.metas?.map((m, mi) => (
                          <div className="col-12 col-md-4" key={mi}>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge rounded-pill text-bg-success bg-opacity-10 text-success border border-success-subtle fw-bold">{m.text1}</span>
                              <span>{m.text2}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </section>

              <section className="py-5">
                <h2 className="h4 mb-3">{app?.faqs?.heading || 'Frequently Asked Questions'}</h2>
                <Accordion defaultActiveKey="0">
                  {app?.faqs?.content?.map((f, idx) => (
                    <Accordion.Item eventKey={`${idx}`} key={idx}>
                      <Accordion.Header>{f.heading}</Accordion.Header>
                      <Accordion.Body>{f.content}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </section>

              <section className="py-5 text-center">
                <p className="text-secondary mb-2">Still exploring the right solution?</p>
                <p className="text-secondary mb-3">Use the Excellenc3 AI Assistant to identify relevant process modules for your business.</p>
                <div className="d-flex gap-2 justify-content-center">
                  <button type="button" className="btn btn-primary rounded-pill px-4" onClick={() => setShowEnquireModal(true)}>Enquire Now</button>
                  <button type="button" className="btn btn-outline-primary rounded-pill px-4" onClick={() => setShowChatBox(true)}>Explore AI Assistant</button>
                </div>
              </section>

              {showChatbox && (
                <ChatBox className="floating" onClose={() => setShowChatBox(false)} />
              )}
            </>
          )}

          <EnquireModal show={showEnquireModal} onClose={() => setShowEnquireModal(false)} onSubmit={onSubmitEnquire} />
        </Container>
      )}
    </div>
  );
}

export default AppDetails;